import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { googleAutocomplete, googlePlaceDetails } from '../../services/google.service';
import { searchPlaces } from '../../services/geocoding.service';
import './MapboxAutocomplete.css'; 

/**
 * Autocomplete component using Google Places API (Native SDK).
 */
const GoogleAutocomplete = ({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Search location with Google...',
  disabled = false,
  required = false,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  debounceMs = 400,
  minChars = 3,
  limit = 5,
  instanceId = 'default', // 'departure' or 'arrival'
}) => {
  const { isLoaded, loadError } = useGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const activeRequest = useRef(0);
  const isSelecting = useRef(false);
  const lastSelectedValue = useRef('');

  // Sync state with props
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  /**
   * Main search function using modern 2026 Google Places API with Mapbox fallback.
   * Optimized with Instance-based sessions and Caching.
   */
  const fetchSuggestions = useCallback(async (query) => {
    if (query.trim().length < minChars) return;

    setLoading(true);
    setError(null);
    const requestId = ++activeRequest.current;

    try {
      // Use the service with the specific instanceId for session tracking
      const predictions = await googleAutocomplete(query, instanceId);

      if (requestId === activeRequest.current) {
        if (predictions.length === 0) {
          // Fallback: Mapbox Geocoding if Google (New) returns nothing
          const geocodingResults = await searchPlaces(query, { limit });
          const processed = geocodingResults.map(f => ({
            id: f.id,
            display: f.place_name,
            secondary: f.text,
            original: f,
            source: 'mapbox'
          }));
          setSuggestions(processed);
          setOpen(processed.length > 0);
        } else {
          const processed = predictions.map(p => ({
            id: p.place_id,
            display: p.main_text,
            secondary: p.secondary_text,
            original: p,
            source: 'google'
          }));
          setSuggestions(processed);
          setOpen(processed.length > 0);
        }
      }
    } catch (err) {
      if (requestId === activeRequest.current) {
        console.error(`[GoogleAutocomplete][${instanceId}] Error:`, err);
        // Fallback on error
        try {
          const geocodingResults = await searchPlaces(query, { limit });
          const processed = geocodingResults.map(f => ({
            id: f.id,
            display: f.place_name,
            secondary: f.text,
            original: f,
            source: 'mapbox'
          }));
          setSuggestions(processed);
          setOpen(processed.length > 0);
        } catch (fErr) {
          setOpen(false);
        }
      }
    } finally {
      if (requestId === activeRequest.current) {
        setLoading(false);
      }
    }
  }, [minChars, limit, instanceId]);

  // Debounced input handler (Strict 400ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (isSelecting.current || inputValue === lastSelectedValue.current) {
      isSelecting.current = false;
      return;
    }

    if (inputValue.length >= minChars) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(inputValue);
      }, debounceMs);
    } else {
      setSuggestions([]);
      setOpen(false);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, fetchSuggestions, debounceMs, minChars]);

  // Handle selection
  const handleSelect = async (item) => {
    isSelecting.current = true;
    lastSelectedValue.current = item.display;
    setOpen(false);
    setSuggestions([]);
    setInputValue(item.display);
    setLoading(true);

    try {
      let coordinates = [0, 0];
      let fullAddress = item.display;

      if (item.source === 'google') {
        // Fetch Place Details (Optimized: only required fields and ends session)
        const details = await googlePlaceDetails(item.id, instanceId);
        const loc = details.geometry.location;
        coordinates = [loc.lng, loc.lat];
        fullAddress = details.formatted_address || item.display;
      } else {
        // Mapbox fallback
        const f = item.original;
        coordinates = f.center || f.geometry.coordinates;
        fullAddress = f.place_name || item.display;
      }

      onSelect?.({
        id: item.id,
        name: fullAddress,
        place_name: fullAddress,
        coordinates: coordinates,
        center: coordinates,
        source: item.source
      });
    } catch (err) {
      console.error(`[GoogleAutocomplete][${instanceId}] Selection failed:`, err);
      setError('Could not retrieve location details.');
    } finally {
      setLoading(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const clearInput = () => {
    setInputValue('');
    setSuggestions([]);
    setOpen(false);
    onChange?.({ target: { value: '' } });
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={`mapbox-autocomplete position-relative ${className}`}>
      <div className="input-wrapper position-relative">
        <input
          ref={inputRef}
          type="text"
          className={`form-control ${inputClassName} ${error || loadError ? 'is-invalid' : ''}`}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange?.(e);
          }}
          onFocus={() => {
            // Re-open if we have results
            if (suggestions.length > 0) setOpen(true);
          }}
          placeholder={!isLoaded ? 'Loading...' : placeholder}
          disabled={disabled || !isLoaded}
          required={required}
          autoComplete="off"
        />
        {inputValue && !disabled && (
          <button type="button" className="btn-clear position-absolute end-0 top-50 translate-middle-y me-2" onClick={clearInput}>
            &times;
          </button>
        )}
        {loading && (
          <div className="position-absolute end-0 top-50 translate-middle-y me-5" style={{ zIndex: 2 }}>
            <div className="spinner-border spinner-border-sm text-secondary" />
          </div>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div className={`suggestions-dropdown position-absolute w-100 bg-white border rounded shadow-sm mt-1 ${dropdownClassName}`} style={{ zIndex: 1050, maxHeight: '250px', overflowY: 'auto' }}>
          {suggestions.map((item, index) => (
            <div
              key={item.id + index}
              className={`suggestion-item p-2 border-bottom cursor-pointer ${index === focusedIndex ? 'bg-light' : ''}`}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <div className="suggestion-name text-truncate">{item.display}</div>
              <small className="suggestion-type text-muted d-block">{item.secondary} {item.source !== 'google' ? `(via ${item.source})` : ''}</small>
            </div>
          ))}
          <div className="p-2 border-top small text-muted bg-light">Powered by Google</div>
        </div>
      )}
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

export default GoogleAutocomplete;
